import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectDropdownsByUser } from '../../store/ticketSlice';
import OverflowTooltip from '../../shared/OverflowTooltip';
import BreakdownHistoryTab from './history/BreakdownHistoryTab';
import ACHistoryTab from './history/ACHistoryTab';
import TaskGeneratorHistoryTab from './history/TaskGeneratorHistoryTab';
// import ReportsHistoryTab from './history/ReportsHistoryTab';
import './StrategistHistory.scss';

const StrategistHistoryView = ({ projectId, onBack }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectFilterAnchor, setProjectFilterAnchor] = useState(null);

  const { projectDropdownsByUser: projectsData, projectDropdownsByUserLoading: projectsLoading } = useSelector((state) => state.tickets);

  const projects = useMemo(() => {
    if (!projectsData) return [];
    if (Array.isArray(projectsData)) return projectsData;
    if (typeof projectsData === 'object') {
      const values = Object.values(projectsData);
      if (values.length > 0 && Array.isArray(values[0])) {
        return values[0];
      }
      return values;
    }
    return [];
  }, [projectsData]);

  useEffect(() => {
    dispatch(fetchProjectDropdownsByUser());
  }, [dispatch]);

  // Always default to all projects selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Keep selectedProjects empty to show "All Projects" by default
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };



  const tabs = [
    { label: t('agent.history.tabs.breakdown'), value: 0 },
    { label: t('agent.history.tabs.ac'), value: 1 },
    { label: t('agent.history.tabs.taskGenerator'), value: 2 },
    // { label: t('agent.history.tabs.reports'), value: 2 },
  ];

  const renderTabContent = () => {
    const projectIds = selectedProjects.length > 0 ? selectedProjects : [];

    switch (activeTab) {
      case 0:
        return <BreakdownHistoryTab selectedProjects={projectIds} />;
      case 1:
        return <ACHistoryTab selectedProjects={projectIds} />;
      case 2:
        return <TaskGeneratorHistoryTab selectedProjects={projectIds} />;
      // case 3:
      //   return <ReportsHistoryTab selectedProjects={projectIds} />;
      default:
        return null;
    }
  };

  return (
    <Box className="strategist-history-view">
      <Box className="strategist-history-header">
        <Box className="strategist-history-header-left">
          <IconButton onClick={onBack} className="strategist-history-back-button">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" className="strategist-history-title">
            {t('agent.history.title')}
          </Typography>
        </Box>
      </Box>

      <Box className="strategist-history-tabs-container">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className="strategist-history-tabs"
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <Box className="strategist-history-project-filter">
          <FormControl className="history-project-select-form-control" disabled={projectsLoading}>
            <InputLabel>{t('agent.filterByProject')}</InputLabel>
            <Select
              value={selectedProjects.length === 0 ? 'all' : selectedProjects.length === 1 ? selectedProjects[0] : 'multiple'}
              onChange={() => {}}
              label={t('agent.filterByProject')}
              open={Boolean(projectFilterAnchor)}
              onOpen={(e) => setProjectFilterAnchor(e.currentTarget)}
              onClose={(e) => {
                if (e?.target?.closest('.MuiMenuItem-root') || 
                    e?.target?.closest('.MuiCheckbox-root') ||
                    e?.target?.closest('.MuiFormControlLabel-root')) {
                  return;
                }
                setProjectFilterAnchor(null);
              }}
              renderValue={(value) => {
                if (value === 'all') return 'All Projects';
                if (value === 'multiple') return `${selectedProjects.length} Projects`;
                const project = projects.find(p => (p.id || p._id || p.project_id) === value);
                const projectName = project ? (project.name || project.title || `Project ${value}`) : value;
                return (
                  <OverflowTooltip 
                    title={projectName} 
                    tooltipWidth={350} 
                    component={Box}
                    sx={{ 
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    }}
                    placement="top-start"
                  >
                    {projectName}
                  </OverflowTooltip>
                );
              }}
              MenuProps={{
                PaperProps: {
                  className: 'strategist-history-dropdown-menu',
                  style: {
                    width: 250,
                    maxHeight: 200,
                    overflow: 'auto'
                  }
                }
              }}
            >
              <MenuItem 
                className="strategist-history-menu-item"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedProjects([]);
                }}
              >
                <Checkbox 
                  checked={selectedProjects.length === 0} 
                  size="small"
                  sx={{ padding: 0, marginRight: '5px', '& .MuiButtonBase-root': { padding: 0 } }}
                />
                All Projects
              </MenuItem>
              {projectsLoading ? (
                <MenuItem disabled className="strategist-history-menu-item">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Loading projects...</span>
                  </Box>
                </MenuItem>
              ) : projects.length === 0 ? (
                <MenuItem disabled className="strategist-history-menu-item">
                  <em>No projects available</em>
                </MenuItem>
              ) : (
                projects.map((project) => {
                  const projId = project.id || project._id || project.project_id;
                  const projectName = project.name || project.title || `Project ${projId}`;
                  return (
                    <MenuItem
                      key={projId}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedProjects.includes(projId)) {
                          setSelectedProjects(prev => prev.filter(id => id !== projId));
                        } else {
                          setSelectedProjects(prev => [...prev, projId]);
                        }
                      }}
                      className="strategist-history-menu-item"
                    >
                      <Checkbox 
                        checked={selectedProjects.includes(projId)} 
                        size="small"
                        sx={{ padding: 0, marginRight: '5px', '& .MuiButtonBase-root': { padding: 0 } }}
                      />
                      <OverflowTooltip title={projectName} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className="strategist-history-content">
        {renderTabContent()}
      </Box>
    </Box>
  );
};

export default StrategistHistoryView;

